import { type z } from 'zod';
import { string as __eliadmin_zod_0, object as __eliadmin_zod_1, boolean as __eliadmin_zod_2 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

import { REGISTER_COPY } from '@eliadmin/core/src/components/dlz/auth/register/register-copy';
import { isValidPhone } from '@eliadmin/core/src/components/dlz/auth/register/register-values';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

/**
 * „Môj profil“ as one flat form. The design marks every field required (the profile feeds the next
 * orders); the company fields count for a company customer only, the delivery address only while it
 * differs from the billing one. The e-mail is the login, shown read-only and never sent. Like the
 * registration and the checkout: no format rules for IČO / DIČ / IČ DPH / PSČ.
 */
export const profileFormSchema = (VM: FormValidationMessages, isCompany: boolean) => {
  const text = __eliadmin_zod_0().trim();

  return __eliadmin_zod_1({
      name: text,
      surname: text,
      email: __eliadmin_zod_0(),
      phone: text,
      company: text,
      ico: text,
      dic: text,
      ic_dph: text,
      address: text,
      city: text,
      postcode: text,
      country: text,
      same_delivery: __eliadmin_zod_2(),
      delivery_address: text,
      delivery_city: text,
      delivery_postcode: text,
      delivery_country: text,
    })
    .superRefine((values, ctx) => {
      const issue = (path: keyof typeof values, message: string) =>
        ctx.addIssue({ code: 'custom', path: [path], message });
      const required = (...paths: (keyof typeof values)[]) => {
        for (const path of paths) {
          if (!values[path]) issue(path, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE());
        }
      };

      required('name', 'surname', 'address', 'city', 'postcode', 'country');

      if (!values.phone) issue('phone', VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE());
      else if (!isValidPhone(values.phone)) issue('phone', REGISTER_COPY.personal.phoneInvalid);

      if (isCompany) required('company', 'ico', 'dic');
      if (!values.same_delivery) {
        required('delivery_address', 'delivery_city', 'delivery_postcode', 'delivery_country');
      }
    });
};

export type ProfileFormSchema = ReturnType<typeof profileFormSchema>;
export type ProfileFormInput = z.input<ProfileFormSchema>;
export type ProfileFormValues = z.output<ProfileFormSchema>;
